import {isMergedRecommendation, type MergedRecommendation, type Recommendation, type TimeEntry} from '@package/shared'
import {useCallback, useState} from 'react'
import dayjs from 'dayjs'
import {TimeEntryContainer} from './TimeEntryContainer.tsx'
import {EditableCard} from '../../components/editableCard/EditableCard.tsx'
import type {DateRange} from '../../components/dateRangeSelector/DateRangeSelector.tsx'
import {AcceptRecommendationForm} from './forms/recommendation/AcceptRecommendationForm.tsx'
import type {AcceptRecommendationRequest, RejectRecommendationRequest} from '@app/api-resolver/router'
import {MergedRecommendationsModal} from './components/MergedRecommendationsModal.tsx'
import {EditModeButtons} from './components/EditModeButtons.tsx'
import {useComputedColorScheme} from '@mantine/core'

type Props = {
  day: DateRange
  recommendation: Recommendation | MergedRecommendation
  onAccept: (request: AcceptRecommendationRequest) => Promise<TimeEntry>
  onReject: (request: RejectRecommendationRequest) => Promise<void>
  disabled: boolean
}

export const RecommendationEditCard = ({
  day,
  recommendation,
  onAccept,
  onReject,
  disabled: containerDisabled,
}: Props) => {
  const colorScheme = useComputedColorScheme()
  const [editMode, setEditMode] = useState<boolean>(false)
  const [mergedModalOpened, setMergedModalOpened] = useState<boolean>(false)

  const [accepting, setAccepting] = useState<boolean>(false)
  const [rejecting, setRejecting] = useState<boolean>(false)

  const handleAccept = useCallback(
    async (request: AcceptRecommendationRequest) => {
      setAccepting(true)
      await onAccept(request).finally(() => setAccepting(false))
      setEditMode(false)
    },
    [onAccept],
  )

  const handleReject = useCallback(async () => {
    setRejecting(true)

    const request: RejectRecommendationRequest = isMergedRecommendation(recommendation)
      ? {recommendationIds: recommendation.recommendations.map(({id}) => id)}
      : {recommendationIds: [recommendation.id]}
    await onReject(request).finally(() => setRejecting(false))
    setEditMode(false)
  }, [onReject, recommendation])

  const disabled = accepting || rejecting || containerDisabled

  return (
    <EditableCard
      p={'xs'}
      bg={colorScheme === 'dark' ? 'var(--mantine-color-dark-4)' : 'var(--mantine-color-green-0)'}
      editMode={editMode}
      onEditModeEntered={() => setEditMode(true)}
      onEditModeLeft={() => setEditMode(false)}
      disabled={disabled}
    >
      {!editMode && (
        <>
          <TimeEntryContainer
            start={dayjs(recommendation.start)}
            end={dayjs(recommendation.end)}
            description={recommendation.description}
            breakHours={recommendation.breakHours}
            sourceType={recommendation.source?.type}
            projectId={recommendation.projectId}
            merged={isMergedRecommendation(recommendation)}
            onMergedBadgeClick={() => setMergedModalOpened(true)}
            disableCompactMode
          />
          <EditModeButtons
            submitVariant={'accept'}
            onSubmit={() => handleAccept(recommendation)}
            submitting={accepting}
            destructiveVariant={'reject'}
            onDestruct={handleReject}
            destructing={rejecting}
            compact
            disabled={disabled}
          />
        </>
      )}

      {editMode && (
        <AcceptRecommendationForm
          recommendation={recommendation}
          day={day}
          onAccept={handleAccept}
          accepting={accepting}
          onReject={handleReject}
          rejecting={rejecting}
          disabled={disabled}
        />
      )}

      {isMergedRecommendation(recommendation) && (
        <MergedRecommendationsModal
          mergedRecommendation={recommendation}
          opened={mergedModalOpened}
          onClose={() => setMergedModalOpened(false)}
        />
      )}
    </EditableCard>
  )
}
